/**
 * Repo Knowledge Library per-repo artifact wire shapes + URL builders (Req 18.4,
 * 18.6) — ADDITIVE.
 *
 * The dashboard SPA already consumes the camelCase per-run `DashboardLibraryIndex`
 * (see `types.ts` / `query.ts`). For the *detail* view we additionally fetch two
 * per-repo **structured** artifacts the Library writes under `library/*`:
 *
 *   - the per-repo `RepoClassification` at
 *     `library/run-date=<D>/run-id=<R>/classification/<safe(repo)>.json`, the
 *     source of the per-decision Evidence the dashboard can request (Req 18.4); and
 *   - the per-repo `NarrativeEnrichment` at
 *     `library/run-date=<D>/run-id=<R>/narrative/<safe(repo)>.json`, the source of
 *     the full `purpose` / `interrelations` narrative and the
 *     `narrative_succeeded` flag.
 *
 * Unlike the dashboard document these structured artifacts are serialized in
 * **snake_case** (via `serialize_repo_classification` and the shell's
 * `_serialize_narrative` in `src/repo_intelligence/library/`), so the interfaces
 * here mirror the Python `RepoClassification` / `NarrativeEnrichment` field names
 * exactly. They are intentionally kept out of `spa/src/types.ts` (which holds the
 * camelCase dashboard family + the snake_case scanner schema) so this module is
 * purely additive and can be removed without touching either family.
 *
 * Every parser is total and defensive: any non-object / malformed input yields
 * `null` (graceful absence) so a missing or older-run artifact never breaks the
 * detail render. The URL builders mirror `buildIndexUrl` / `buildLibraryIndexUrl`
 * (root-absolute, run-date/run-id URL-encoded) and `safeObjectKey` ports the
 * Python `safe_object_key` byte-for-byte so the repo segment matches the object
 * the shell wrote.
 */

// ---------------------------------------------------------------------------
// Confidence (mirrors library/models.py `Confidence`)
// ---------------------------------------------------------------------------

/** Classification confidence level — closed set, mirrors `Confidence`. */
export type LibraryConfidence = "high" | "medium" | "low";

// ---------------------------------------------------------------------------
// Classification artifact (snake_case — mirrors `RepoClassification`)
// ---------------------------------------------------------------------------

/**
 * One grounded justification for a classification decision. Mirrors
 * `Evidence` in `library/models.py` exactly: `field_refs` are defined
 * `RepoScanResult` field references (e.g. `infra.terraform_found`), `rule_id`
 * the matched mapping-rule id, `recency_days` the computed recency where
 * relevant, `secret_like` marks a Secret_Like_Variable referenced by name only
 * (its value is never stored — Req 19.5), and `note` an optional human note.
 *
 * Note: the Python model carries NO `kind`/`ref`/`path` fields; evidence is
 * grounded via `field_refs` + `rule_id`. These names match the wire exactly.
 */
export interface ClassificationEvidence {
  field_refs: string[];
  rule_id: string | null;
  recency_days: number | null;
  secret_like: boolean;
  note: string | null;
}

/** Repo_Type decision (mirrors `RepoTypeClassification`). */
export interface RepoTypeDecision {
  repo_type: string;
  confidence: LibraryConfidence;
  evidence: ClassificationEvidence[];
}

/** Lifecycle_Status decision (mirrors `LifecycleClassification`). */
export interface LifecycleDecision {
  lifecycle_status: string;
  confidence: LibraryConfidence;
  recency_days: number | null;
  evidence: ClassificationEvidence[];
}

/** Ownership decision (mirrors `OwnershipAssignment`). */
export interface OwnershipDecision {
  owning_team: string;
  confidence: LibraryConfidence;
  evidence: ClassificationEvidence[];
}

/** Application mapping decision (mirrors `ApplicationMapping`). */
export interface ApplicationDecision {
  application: string;
  confidence: LibraryConfidence;
  matched_rule_id: string | null;
  evidence: ClassificationEvidence[];
}

/** Product mapping decision (mirrors `ProductMapping`). */
export interface ProductDecision {
  product: string;
  confidence: LibraryConfidence;
  matched_rule_id: string | null;
  evidence: ClassificationEvidence[];
}

/**
 * The per-repo structured classification document. Mirrors `RepoClassification`
 * in `library/models.py`, serialized snake_case via
 * `serialize_repo_classification`.
 */
export interface RepoClassificationArtifact {
  schema_version: number;
  repo_name: string;
  scan_id: string;
  mapping_config_version: string;
  repo_type: RepoTypeDecision;
  lifecycle: LifecycleDecision;
  ownership: OwnershipDecision;
  application: ApplicationDecision;
  product: ProductDecision;
}

// ---------------------------------------------------------------------------
// Narrative artifact (snake_case — mirrors `NarrativeEnrichment`)
// ---------------------------------------------------------------------------

/** Reproducibility metadata stamped onto a narrative (mirrors `Provenance`). */
export interface NarrativeProvenance {
  schema_version: number;
  run_id: string;
  run_date: string;
  generated_at: string;
  mapping_config_version: string | null;
  bedrock_model_id: string | null;
  prompt_version: string | null;
  narrative_input_fingerprint: string | null;
}

/**
 * The per-repo LLM narrative. Mirrors `NarrativeEnrichment` in
 * `library/models.py`, serialized snake_case via the shell's
 * `_serialize_narrative`. Each text field is free text or the explicit
 * `"unknown"` / `"unavailable"` marker; `narrative_succeeded` is `false` on any
 * failure, in which case the three text fields are `"unavailable"` (Req 14.1).
 * The artifact is already secret-safe — no value is ever stored.
 */
export interface NarrativeArtifact {
  repo_name: string;
  short_description: string;
  purpose: string;
  interrelations: string;
  field_sources: Record<string, string[]>;
  narrative_succeeded: boolean;
  provenance: NarrativeProvenance | null;
}

// ---------------------------------------------------------------------------
// Key sanitizer + URL builders
// ---------------------------------------------------------------------------

/**
 * Port of `repo_intelligence.handlers._common.safe_object_key`: replace every
 * character outside `[A-Za-z0-9._-]` with `_`, mapping an empty result to the
 * literal `"repo"`. Mirrors the Python `"".join(...) or "repo"` exactly so the
 * sanitized segment matches the object key the Library shell wrote.
 */
export function safeObjectKey(repoName: string): string {
  const replaced = repoName.replace(/[^A-Za-z0-9._-]/g, "_");
  return replaced === "" ? "repo" : replaced;
}

/**
 * Build the per-repo classification artifact URL, matching the Library output
 * key `library/run-date=<D>/run-id=<R>/classification/<safe(repo)>.json`. The
 * run-date/run-id segments are URL-encoded (mirroring `buildIndexUrl`); the repo
 * segment is already sanitized via {@link safeObjectKey}. The path is
 * root-absolute so it resolves against the deployed origin regardless of the
 * current client route and maps to the CloudFront `library/*` cache behavior.
 */
export function buildClassificationArtifactUrl(
  runDate: string,
  runId: string,
  repoName: string,
): string {
  const date = encodeURIComponent(runDate);
  const id = encodeURIComponent(runId);
  return `/library/run-date=${date}/run-id=${id}/classification/${safeObjectKey(
    repoName,
  )}.json`;
}

/**
 * Build the per-repo narrative artifact URL, matching the Library output key
 * `library/run-date=<D>/run-id=<R>/narrative/<safe(repo)>.json`. Same encoding
 * convention as {@link buildClassificationArtifactUrl}.
 */
export function buildNarrativeArtifactUrl(
  runDate: string,
  runId: string,
  repoName: string,
): string {
  const date = encodeURIComponent(runDate);
  const id = encodeURIComponent(runId);
  return `/library/run-date=${date}/run-id=${id}/narrative/${safeObjectKey(
    repoName,
  )}.json`;
}

// ---------------------------------------------------------------------------
// Defensive parsers (graceful absence — Req 18.6)
// ---------------------------------------------------------------------------

/** True for a non-null, non-array plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Coerce an unknown to one of the three confidence levels, defaulting to `low`. */
function toConfidence(value: unknown): LibraryConfidence {
  return value === "high" || value === "medium" || value === "low"
    ? value
    : "low";
}

/** Coerce an unknown to a string, or `fallback` when it is not a string. */
function toStringOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

/** Coerce an unknown to a string, or `null` when it is not a string. */
function toStringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/** Coerce an unknown to a finite number, or `null` otherwise. */
function toNumberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Coerce an unknown to a `string[]`, dropping non-string entries. */
function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

/** Normalize one evidence item; defensive over a malformed entry. */
function normalizeEvidence(value: unknown): ClassificationEvidence {
  const record = isRecord(value) ? value : {};
  return {
    field_refs: toStringArray(record.field_refs),
    rule_id: toStringOrNull(record.rule_id),
    recency_days: toNumberOrNull(record.recency_days),
    secret_like: record.secret_like === true,
    note: toStringOrNull(record.note),
  };
}

/** Normalize an evidence list; a non-array yields `[]`. */
function normalizeEvidenceList(value: unknown): ClassificationEvidence[] {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeEvidence);
}

/**
 * Parse a fetched classification artifact body into a
 * {@link RepoClassificationArtifact}, defaulting every field. Returns `null`
 * only when the body is not a plain object (graceful absence). Decisions whose
 * value is `"unknown"` are preserved verbatim so the explicit `unknown` marker
 * surfaces in the UI (Req 18.6).
 */
export function parseClassificationArtifact(
  raw: unknown,
): RepoClassificationArtifact | null {
  if (!isRecord(raw)) return null;

  const repoType = isRecord(raw.repo_type) ? raw.repo_type : {};
  const lifecycle = isRecord(raw.lifecycle) ? raw.lifecycle : {};
  const ownership = isRecord(raw.ownership) ? raw.ownership : {};
  const application = isRecord(raw.application) ? raw.application : {};
  const product = isRecord(raw.product) ? raw.product : {};

  return {
    schema_version: toNumberOrNull(raw.schema_version) ?? 0,
    repo_name: toStringOr(raw.repo_name, ""),
    scan_id: toStringOr(raw.scan_id, ""),
    mapping_config_version: toStringOr(raw.mapping_config_version, ""),
    repo_type: {
      repo_type: toStringOr(repoType.repo_type, "unknown"),
      confidence: toConfidence(repoType.confidence),
      evidence: normalizeEvidenceList(repoType.evidence),
    },
    lifecycle: {
      lifecycle_status: toStringOr(lifecycle.lifecycle_status, "unknown"),
      confidence: toConfidence(lifecycle.confidence),
      recency_days: toNumberOrNull(lifecycle.recency_days),
      evidence: normalizeEvidenceList(lifecycle.evidence),
    },
    ownership: {
      owning_team: toStringOr(ownership.owning_team, "unknown"),
      confidence: toConfidence(ownership.confidence),
      evidence: normalizeEvidenceList(ownership.evidence),
    },
    application: {
      application: toStringOr(application.application, "unknown"),
      confidence: toConfidence(application.confidence),
      matched_rule_id: toStringOrNull(application.matched_rule_id),
      evidence: normalizeEvidenceList(application.evidence),
    },
    product: {
      product: toStringOr(product.product, "unknown"),
      confidence: toConfidence(product.confidence),
      matched_rule_id: toStringOrNull(product.matched_rule_id),
      evidence: normalizeEvidenceList(product.evidence),
    },
  };
}

/** Normalize a `field_sources` map; non-object yields `{}`. */
function normalizeFieldSources(value: unknown): Record<string, string[]> {
  if (!isRecord(value)) return {};
  const out: Record<string, string[]> = {};
  for (const [key, entry] of Object.entries(value)) {
    out[key] = toStringArray(entry);
  }
  return out;
}

/** Parse a narrative provenance block; `null` when absent/malformed. */
function parseProvenance(value: unknown): NarrativeProvenance | null {
  if (!isRecord(value)) return null;
  return {
    schema_version: toNumberOrNull(value.schema_version) ?? 0,
    run_id: toStringOr(value.run_id, ""),
    run_date: toStringOr(value.run_date, ""),
    generated_at: toStringOr(value.generated_at, ""),
    mapping_config_version: toStringOrNull(value.mapping_config_version),
    bedrock_model_id: toStringOrNull(value.bedrock_model_id),
    prompt_version: toStringOrNull(value.prompt_version),
    narrative_input_fingerprint: toStringOrNull(
      value.narrative_input_fingerprint,
    ),
  };
}

/**
 * Parse a fetched narrative artifact body into a {@link NarrativeArtifact},
 * defaulting every field to the explicit `"unavailable"` marker (Req 14.1,
 * 18.6). Returns `null` only when the body is not a plain object (graceful
 * absence). `narrative_succeeded` is `true` only when the wire value is exactly
 * `true`.
 */
export function parseNarrativeArtifact(raw: unknown): NarrativeArtifact | null {
  if (!isRecord(raw)) return null;
  return {
    repo_name: toStringOr(raw.repo_name, ""),
    short_description: toStringOr(raw.short_description, "unavailable"),
    purpose: toStringOr(raw.purpose, "unavailable"),
    interrelations: toStringOr(raw.interrelations, "unavailable"),
    field_sources: normalizeFieldSources(raw.field_sources),
    narrative_succeeded: raw.narrative_succeeded === true,
    provenance: parseProvenance(raw.provenance),
  };
}
